import clsx from 'clsx'
import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
} from 'react'
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import InputTypeText from '../input-type-text/input-type-text'
import { INPUT_TYPES } from '../input-type-text/utils/input-types.enum'
import { Button, BUTTON_COLOR, BUTTON_SIZE, BUTTON_WEIGHT } from '../../atoms'

interface ITextFieldProps {
  style?: CSSProperties
  className?: string
  placeholder?: string
}
export interface INumberInputRange {
  label?: ReactNode
  value?: number
  onChange?: (value: number | undefined) => void
  min?: number
  max?: number
  textField?: ITextFieldProps
  disabled?: boolean
  height?: number
  isFloat?: boolean
  isPairOnly?: boolean
  handleErrorFromParent?: {
    key: string
    setError: (key: string, message: string | null) => void
  }
  adultPicker?: boolean
}

export interface NumberInputRangeRef extends HTMLDivElement {
  onChange: () => void
}

export const NumberInputRange = forwardRef(
  (
    {
      label,
      value,
      onChange,
      min = 0,
      max = 9999,
      textField,
      disabled = false,
      height = 25,
      isFloat = false,
      isPairOnly = false,
      handleErrorFromParent,
      adultPicker = false,
    }: INumberInputRange,
    refParent,
  ) => {
    const intl = useIntl()
    const {
      control,
      getValues,
      setValue,
      trigger,
      formState: { isValid, errors },
    } = useForm({
      mode: 'onChange',
      defaultValues: {
        number: value ? value.toString() : null,
      },
    })

    useEffect(() => {
      setValue('number', value ? value.toString() : null)
    }, [value])

    useImperativeHandle(refParent, () => ({
      onChange: () => {
        const currentValue = getValues('number')
        const value = handleValue({ currentValue, increment: 0 })

        setValue('number', value)
        if (onChange) {
          onChange(value ? parseNumber(value) : 0)
        }
      },
    }))

    useEffect(() => {
      trigger('number')
    }, [trigger])

    useEffect(() => {
      if (handleErrorFromParent) {
        const { key, setError } = handleErrorFromParent
        setError(key, errors?.number?.message ?? null)
      }
    }, [errors, isValid])

    const parseNumber = (value: string) => {
      return isFloat === true ? parseFloat(value) : parseInt(value)
    }

    const handleValue = ({
      currentValue,
      increment,
    }: {
      currentValue: string | null
      increment: 1 | -1 | 0
    }) => {
      if (!currentValue) {
        if (increment === 1) {
          return min ? min.toString() : '1'
        }
        return null
      }

      const numberValue = parseNumber(currentValue ?? 0)

      const nextValue = numberValue + increment

      if (nextValue === 0) {
        return '0'
      }

      if (nextValue < min) {
        return min.toString()
      }

      if (nextValue > max) {
        return max.toString()
      }

      return nextValue.toString()
    }

    const handleChange = ({
      field,
      increment,
    }: {
      field: ControllerRenderProps<
        {
          number: string | null
        },
        'number'
      >
      increment: 1 | -1
    }) => {
      const currentValue = field.value
      const value = handleValue({ currentValue, increment })

      field.onChange(value ?? '0')
      if (onChange) {
        onChange(value ? parseNumber(value) : 0)
      }
    }

    return (
      <div
        className={clsx(s.container, { [s.containerAdultPicker]: adultPicker })}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {label && <div className={s.label}>{label} </div>}
        <div className={s.buttons}>
          <Controller
            control={control}
            name='number'
            render={({ field }) => {
              return (
                <Button
                  disabled={disabled || parseNumber(field.value || '0') <= min}
                  onClick={() => {
                    handleChange({
                      field,
                      increment: -1,
                    })
                  }}
                  size={BUTTON_SIZE.NORMAL}
                  weight={BUTTON_WEIGHT.OUTLINED}
                  color={BUTTON_COLOR.COLOR}
                  style={{
                    width: height,
                    height: height,
                    padding: 0,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  loading={false}
                  data-testid='number-input-range-decrement'
                >
                  -
                </Button>
              )
            }}
          />
          <InputTypeText
            control={control}
            name='number'
            rules={{
              validate: (value) => {
                if (isPairOnly && value && parseInt(value) % 2 !== 0) {
                  return intl.formatMessage({
                    id: 'ui.NumberInputRange.selectOddNumber',
                    defaultMessage: 'Veuillez saisir un nombre pair',
                  })
                }
                return true
              },
            }}
            placeholder={textField?.placeholder}
            inputType={isFloat ? INPUT_TYPES.FLOAT : INPUT_TYPES.INT}
            className={clsx(s.textField, textField?.className)}
            disable={disabled}
            style={{ height, ...textField?.style }}
            onBlur={() => {
              const currentValue = getValues('number')
              const value = handleValue({ currentValue, increment: 0 })

              setValue('number', value)
              if (onChange) {
                onChange(value ? parseNumber(value) : 0)
              }
            }}
            value={getValues('number') ?? undefined}
          />
          <Controller
            control={control}
            name='number'
            render={({ field }) => {
              return (
                <Button
                  disabled={
                    disabled || parseNumber(field.value || '0') + 1 > max
                  }
                  onClick={() => {
                    handleChange({
                      field,
                      increment: 1,
                    })
                  }}
                  size={BUTTON_SIZE.NORMAL}
                  weight={BUTTON_WEIGHT.OUTLINED}
                  color={BUTTON_COLOR.COLOR}
                  style={{
                    width: height,
                    height: height,
                    padding: 0,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  loading={false}
                  data-testid='number-input-range-increment'
                >
                  +
                </Button>
              )
            }}
          />
        </div>
      </div>
    )
  },
)
